(() => {
  const namespaceKey = 'GuruShopHomepageMedia';
  const markerSelector = 'script[data-guru-homepage-media-root]';
  const existingApi = window[namespaceKey];

  if (existingApi?.scan) {
    existingApi.scan(document);
    return;
  }

  const initializedRoots = new WeakSet();
  const cleanupByRoot = new WeakMap();

  const callSafely = (callback) => {
    try {
      callback();
    } catch (_error) {
      // A detached or browser-owned media object may throw; poster stays usable.
    }
  };

  function initRoot(sectionRoot) {
    if (!sectionRoot || initializedRoots.has(sectionRoot)) return false;

    const videos = [...sectionRoot.querySelectorAll('[data-guru-deferred-video]')];
    if (videos.length === 0 || !('IntersectionObserver' in window)) return false;

    const abortController = new AbortController();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const compactViewport = window.matchMedia('(max-width: 767px)');
    const states = new Map();

    const isPermittedSource = (value) => {
      if (typeof value !== 'string' || value.length === 0 || value.length > 2048 || value !== value.trim()) return false;
      try {
        const url = new URL(value, window.location.origin);
        const directShopifyCdn = url.hostname === 'cdn.shopify.com' || url.hostname === 'videos.shopifycdn.com';
        const storeThemeAsset = url.origin === window.location.origin && url.pathname.startsWith('/cdn/shop/');
        return (
          url.protocol === 'https:'
          && !url.username
          && !url.password
          && (!url.port || url.port === '443')
          && (directShopifyCdn || storeThemeAsset)
          && url.pathname.toLowerCase().endsWith('.mp4')
        );
      } catch (_error) {
        return false;
      }
    };

    const currentSource = (video) => {
      const candidate = compactViewport.matches
        ? video.dataset.guruMediaMobileSrc || video.dataset.guruMediaDesktopSrc
        : video.dataset.guruMediaDesktopSrc || video.dataset.guruMediaMobileSrc;
      return isPermittedSource(candidate) ? candidate : '';
    };

    const updateToggle = (state, playing = false) => {
      const { toggle } = state;
      if (!toggle) return;
      toggle.setAttribute('aria-pressed', String(playing));
      toggle.setAttribute('aria-label', playing ? toggle.dataset.pauseLabel : toggle.dataset.playLabel);
    };

    const showPoster = (state) => {
      state.shell?.classList.remove('is-playing');
      updateToggle(state, false);
    };

    const pause = (state) => {
      state.playToken += 1;
      callSafely(() => state.video.pause());
      showPoster(state);
    };

    const detachSource = (state) => {
      pause(state);
      if (!state.sourceAttached && !state.video.hasAttribute('src')) return;
      state.sourceAttached = false;
      state.video.removeAttribute('src');
      delete state.video.dataset.guruMediaSourceAttached;
      callSafely(() => state.video.load());
    };

    const failPlayback = (state, token) => {
      if (state.destroyed || token !== state.playToken) return;
      state.manualPaused = true;
      state.userRequested = false;
      state.shell?.classList.add('has-media-error');
      detachSource(state);
    };

    const requestPlayback = (state) => {
      if (
        state.destroyed
        || reducedMotion.matches
        || document.hidden
        || !state.visible
        || !state.sourceAttached
        || state.manualPaused
      ) return;

      const token = ++state.playToken;
      let playResult;
      try {
        playResult = state.video.play();
        Promise.resolve(playResult).then(
          () => {
            if (state.destroyed || token !== state.playToken || reducedMotion.matches || document.hidden || !state.visible || state.manualPaused || state.video.paused) {
              callSafely(() => state.video.pause());
              return;
            }
            state.shell?.classList.remove('has-media-error');
            state.shell?.classList.add('is-playing');
            updateToggle(state, true);
          },
          () => failPlayback(state, token),
        );
      } catch (_error) {
        failPlayback(state, token);
      }
    };

    const attachSource = (state) => {
      if (state.destroyed || reducedMotion.matches || document.hidden || !state.visible || state.sourceAttached) return false;
      const source = currentSource(state.video);
      if (!source) return false;

      state.video.src = source;
      state.video.dataset.guruMediaSourceAttached = 'true';
      state.sourceAttached = true;
      callSafely(() => state.video.load());
      return true;
    };

    const syncState = (state) => {
      if (state.destroyed) return;
      const hasPermittedSource = Boolean(currentSource(state.video));
      if (reducedMotion.matches || !hasPermittedSource) {
        detachSource(state);
        state.toggle.hidden = true;
        return;
      }

      state.toggle.hidden = false;
      if (document.hidden || !state.visible) {
        pause(state);
        return;
      }

      attachSource(state);
      const shouldPlay = state.userRequested || (state.autoplay && !state.manualPaused);
      if (shouldPlay) requestPlayback(state);
    };

    const intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const state = states.get(entry.target);
        if (!state || state.destroyed) return;
        state.visible = entry.isIntersecting && entry.intersectionRatio > 0;
        syncState(state);
      });
    }, { threshold: [0, 0.01, 0.25] });

    videos.forEach((video) => {
      const shell = video.closest('[data-guru-media-shell]');
      const toggle = shell?.querySelector('[data-guru-media-toggle]');
      if (!shell || !toggle) return;

      const state = {
        autoplay: video.dataset.guruMediaAutoplay === 'true',
        destroyed: false,
        manualPaused: false,
        playToken: 0,
        shell,
        sourceAttached: false,
        toggle,
        userRequested: false,
        video,
        visible: false,
      };
      states.set(video, state);
      updateToggle(state, false);
      toggle.hidden = reducedMotion.matches || !currentSource(video);

      toggle.addEventListener('click', () => {
        if (state.destroyed || reducedMotion.matches) return;
        const isPlaying = !video.paused && shell.classList.contains('is-playing');
        if (isPlaying) {
          state.manualPaused = true;
          state.userRequested = false;
          pause(state);
          return;
        }

        state.manualPaused = false;
        state.userRequested = true;
        if (!state.sourceAttached) attachSource(state);
        requestPlayback(state);
      }, { signal: abortController.signal });

      video.addEventListener('error', () => failPlayback(state, state.playToken), { signal: abortController.signal });
      video.addEventListener('play', () => {
        if (state.destroyed || reducedMotion.matches || document.hidden || !state.visible || state.manualPaused) {
          callSafely(() => video.pause());
          showPoster(state);
          return;
        }
        shell.classList.remove('has-media-error');
        shell.classList.add('is-playing');
        updateToggle(state, true);
      }, { signal: abortController.signal });
      video.addEventListener('pause', () => {
        if (!state.destroyed) showPoster(state);
      }, { signal: abortController.signal });
      video.addEventListener('ended', () => {
        if (!video.loop) showPoster(state);
      }, { signal: abortController.signal });
      intersectionObserver.observe(video);
    });

    if (states.size === 0) {
      abortController.abort();
      intersectionObserver.disconnect();
      return false;
    }

    const syncAll = () => states.forEach(syncState);
    const syncDocumentVisibility = () => states.forEach(syncState);
    const swapResponsiveSources = () => {
      states.forEach((state) => {
        detachSource(state);
        syncState(state);
      });
    };
    reducedMotion.addEventListener('change', syncAll);
    compactViewport.addEventListener('change', swapResponsiveSources);
    document.addEventListener('visibilitychange', syncDocumentVisibility, { signal: abortController.signal });

    const cleanup = () => {
      abortController.abort();
      intersectionObserver.disconnect();
      reducedMotion.removeEventListener('change', syncAll);
      compactViewport.removeEventListener('change', swapResponsiveSources);
      states.forEach((state) => {
        state.destroyed = true;
        state.playToken += 1;
        detachSource(state);
        state.toggle.hidden = true;
      });
      states.clear();
      delete sectionRoot.dataset.guruHomepageMediaBound;
      initializedRoots.delete(sectionRoot);
      cleanupByRoot.delete(sectionRoot);
    };

    initializedRoots.add(sectionRoot);
    cleanupByRoot.set(sectionRoot, cleanup);
    sectionRoot.dataset.guruHomepageMediaBound = 'true';
    return true;
  }

  function cleanupRoot(sectionRoot) {
    const cleanup = cleanupByRoot.get(sectionRoot);
    if (!cleanup) return false;
    cleanup();
    return true;
  }

  function initById(sectionRootId) {
    if (!sectionRootId) return false;
    return initRoot(document.getElementById(sectionRootId));
  }

  function scan(scope = document) {
    const markers = [];
    if (scope.matches?.(markerSelector)) markers.push(scope);
    markers.push(...scope.querySelectorAll?.(markerSelector) || []);
    markers.forEach((marker) => initById(marker.dataset.guruHomepageMediaRoot));
  }

  document.addEventListener('shopify:section:load', (event) => {
    initRoot(event.target);
    scan(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => cleanupRoot(event.target));

  window[namespaceKey] = { cleanupRoot, initById, initRoot, scan };
  scan(document);

  const mutationObserver = new MutationObserver((records) => {
    records.forEach((record) => record.removedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;
      if (node.matches('[data-guru-homepage-media-bound]')) cleanupRoot(node);
      node.querySelectorAll('[data-guru-homepage-media-bound]').forEach(cleanupRoot);
    }));
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node instanceof Element) scan(node);
    }));
  });
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
